// Thin client for the org-scoped RBAC / permission endpoints (backend
// `app/routers/permission.py`, prefix `/permissions`). Org roles are CUSTOM,
// org-owned rows (name + arbitrary permission strings), NOT the fixed facility
// role enum (owner/manager/front_desk/coach) used by the backend `can()` check.
// So the Roles tab creates roles; the facility role enum is only what a
// FacilityAssignment maps a user to via role_id.
import { httpClient } from "@bw/shared/api";
import type {
  GrantablePermissionsResponse,
  GroupRoleAssignmentResponse,
  OrgRoleResponse,
  Schema,
  StaffGroupMemberResponse,
  StaffGroupResponse,
} from "@bw/shared/types/generated";

// ---------------------------------------------------------------------------
// Response models — sourced from the generated OpenAPI types (ADR-005), which
// mirror backend `common/models.py`. OrgRoleResponse carries member_count:int
// (the DISTINCT head-count of users holding the role); only the list endpoint
// computes it. Permissions are raw action strings, e.g. "facility:read".
// ---------------------------------------------------------------------------

export type OrgRole = OrgRoleResponse;

export function listGrantableFacilityRoles(
  facilityId: string,
): Promise<OrgRole[]> {
  return httpClient.get<OrgRole[]>(
    `/permissions/facilities/${encodeURIComponent(facilityId)}/grantable-roles`,
  );
}

// Server-authoritative grantable/reserved permission set for the editor's
// toggle-locking. `permissions` = what the caller may grant in this org (owner
// → all defined actions; non-owner → held actions minus the reserved set),
// `reserved` = the owner-only ADMIN_RESERVED set, `is_owner` = implicit-owner.
// The FE NEVER derives this locally — it comes straight from the backend.
export type GrantablePermissions = GrantablePermissionsResponse;

// OrgMembershipResponse: membership_id, user_id, org_id, role_id, created_at.
// NOTE: carries only the user_id (Firebase UID) + role_id — no display_name /
// email / role name. Resolve those by joining against OrgRole[] (role_id →
// name) client-side; display names are not provided by this endpoint.
export interface OrgMembership {
  membership_id: string;
  user_id: string;
  org_id: string;
  role_id: string;
  created_at: string;
}

// FacilityAssignmentResponse: assignment_id, user_id, facility_id, role_id,
// created_at. Identity is resolved separately through the authorized batch API.
export type FacilityAssignment = Schema["FacilityAssignmentResponse"];
export type StaffMemberIdentity = Schema["StaffMemberIdentity"];
export type StaffIdentityResolution = Schema["StaffIdentityResolutionResponse"];

// ---------------------------------------------------------------------------
// Request bodies — mirror backend EXACTLY.
// ---------------------------------------------------------------------------

// OrgRoleCreate: { name, permissions[] }. There is NO `description` field on
// the backend create model despite the flow mockup.
export interface OrgRoleCreate {
  name: string;
  permissions: string[];
}

// OrgRoleUpdate: PATCH /roles/{role_id} — both fields optional.
export interface OrgRoleUpdate {
  name?: string;
  permissions?: string[];
}

// Friendly display labels for EVERY permission action the backend can return
// (facility + org + community). Source of truth: backend common/permissions.py.
// A role fetched from the backend may carry org-level actions (e.g. a Manager
// role holds org:roles_manage), so the display map must cover all scopes — never
// show a raw "facility:program" / "org:staff_manage" code to an operator.
export const PERMISSION_LABELS: Record<string, string> = {
  "facility:read": "View facility data",
  "facility:read_internal": "View internal facility data",
  "facility:manage": "Edit facility settings",
  "facility:schedule": "Manage calendar",
  "facility:price": "Set pricing rules",
  "facility:court": "Manage courts",
  "facility:tournament": "Manage tournaments",
  "facility:league": "Manage leagues",
  "facility:checkin": "Check in players",
  "facility:queue": "Run the drop-in queue",
  "facility:assess": "Skill assessments",
  "facility:program": "Manage classes",
  "facility:enroll": "Enroll players",
  "facility:coach": "Manage coaches",
  "facility:community": "Manage community",
  "facility:inquiry_reply": "Reply to inquiries",
  "facility:inquiry_triage": "Manage inquiries",
  "facility:refund_override": "Override refund amount",
  "org:roles_manage": "Manage roles",
  "org:staff_manage": "Manage team access",
  "community:read": "View community",
  "community:post": "Post in community",
  "community:comment": "Comment in community",
  "community:moderate": "Moderate community",
  "community:manage": "Manage community",
};

// The facility actions a custom role can be granted (role-create checkbox list).
// Derived from PERMISSION_LABELS so labels never drift from the display map.
// Covers every facility action (the ADR-006 program/coaching set + queue and
// community, which the drop-in-queue and facility-community modules added).
const FACILITY_ACTION_VALUES = [
  "facility:read",
  "facility:read_internal",
  "facility:manage",
  "facility:schedule",
  "facility:price",
  "facility:court",
  "facility:tournament",
  "facility:league",
  "facility:checkin",
  "facility:queue",
  "facility:assess",
  "facility:program",
  "facility:enroll",
  "facility:coach",
  "facility:community",
  "facility:inquiry_reply",
  "facility:inquiry_triage",
] as const;

export const FACILITY_ACTIONS: { value: string; label: string }[] =
  FACILITY_ACTION_VALUES.map((value) => ({
    value,
    label: PERMISSION_LABELS[value],
  }));

// ---------------------------------------------------------------------------
// Permission GROUPS — the role editor renders toggles grouped under 4 plain-
// English area headings rather than one flat 15-row list. The SAME grouping
// drives the roles-LIST summary ("Operations + Programs"): a role's group
// summary is the set of group titles whose perms it holds. Source of the perm
// → group mapping: backend common/permissions.py action namespaces.
// ---------------------------------------------------------------------------

export interface PermissionGroup {
  title: string;
  permissions: string[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    title: "Operations",
    permissions: [
      "facility:read",
      "facility:read_internal",
      "facility:schedule",
      "facility:court",
      "facility:checkin",
      "facility:queue",
      "facility:tournament",
      "facility:league",
      "facility:assess",
      "facility:community",
      "facility:inquiry_reply",
      "facility:inquiry_triage",
    ],
  },
  {
    title: "Pricing",
    permissions: ["facility:price"],
  },
  {
    title: "Programs",
    permissions: ["facility:program", "facility:enroll", "facility:coach"],
  },
  {
    title: "Staff-Admin",
    permissions: [
      "facility:manage",
      "org:roles_manage",
      "org:staff_manage",
      "facility:refund_override",
    ],
  },
];

// Reverse lookup: permission action → its group title. Used by the list summary
// to collapse a role's raw permissions into the set of AREAS it touches.
const PERMISSION_TO_GROUP: Record<string, string> = Object.fromEntries(
  PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => [p, g.title])),
);

// The ordered, deduped list of group titles a role's permissions touch, e.g.
// ["facility:read","facility:checkin","facility:program"] → ["Operations",
// "Programs"]. Drives the one-line list summary; unknown perms are ignored so a
// future action never leaks a raw code into the summary.
export function roleGroupSummary(permissions: string[]): string[] {
  const titles = new Set<string>();
  for (const perm of permissions) {
    const group = PERMISSION_TO_GROUP[perm];
    if (group) titles.add(group);
  }
  return PERMISSION_GROUPS.map((g) => g.title).filter((t) => titles.has(t));
}

// ---------------------------------------------------------------------------
// Roles — org-scoped CUSTOM roles
// GET    /permissions/orgs/{orgId}/roles
// POST   /permissions/orgs/{orgId}/roles
// PATCH  /permissions/roles/{roleId}        (note: NOT org-scoped path)
// DELETE /permissions/roles/{roleId}        (note: NOT org-scoped path)
// ---------------------------------------------------------------------------

export function listOrgRoles(orgId: string): Promise<OrgRole[]> {
  return httpClient.get<OrgRole[]>(`/permissions/orgs/${orgId}/roles`);
}

export function createOrgRole(
  orgId: string,
  body: OrgRoleCreate,
): Promise<OrgRole> {
  return httpClient.post<OrgRole>(`/permissions/orgs/${orgId}/roles`, body);
}

// PATCH /permissions/roles/{roleId} — edit a role's name and/or permissions.
// Both fields are optional (OrgRoleUpdate). The backend re-checks the
// escalation guard server-side; a denied grant surfaces as a 403 the caller
// must NOT swallow.
export function updateRole(
  roleId: string,
  body: OrgRoleUpdate,
): Promise<OrgRole> {
  return httpClient.patch<OrgRole>(`/permissions/roles/${roleId}`, body);
}

export function deleteRole(roleId: string): Promise<void> {
  return httpClient.delete<void>(`/permissions/roles/${roleId}`);
}

// GET /permissions/orgs/{orgId}/grantable-permissions — the server-authoritative
// set the role editor uses to LOCK toggles. The FE never derives which perms a
// caller may grant; it asks the server.
export function getGrantablePermissions(
  orgId: string,
): Promise<GrantablePermissions> {
  return httpClient.get<GrantablePermissions>(
    `/permissions/orgs/${orgId}/grantable-permissions`,
  );
}

// ---------------------------------------------------------------------------
// Org membership
// POST   /permissions/orgs/{orgId}/members?user_id=&role_id=   (QUERY params!)
// DELETE /permissions/orgs/{orgId}/members/{userId}
// IMPORTANT: assign_membership takes user_id + role_id as QUERY params, not a
// JSON body. The flow's `{ user_id, role_id }` body shape is wrong.
// ---------------------------------------------------------------------------

export function inviteOrgMember(
  orgId: string,
  body: { user_id: string; role_id: string },
): Promise<OrgMembership> {
  const qs = new URLSearchParams({
    user_id: body.user_id,
    role_id: body.role_id,
  });
  return httpClient.post<OrgMembership>(
    `/permissions/orgs/${orgId}/members?${qs.toString()}`,
  );
}

export function removeOrgMember(orgId: string, userId: string): Promise<void> {
  return httpClient.delete<void>(
    `/permissions/orgs/${orgId}/members/${encodeURIComponent(userId)}`,
  );
}

// ---------------------------------------------------------------------------
// Facility staff assignment
// GET    /permissions/facilities/{facilityId}/staff
// POST   /permissions/facilities/{facilityId}/staff?user_id=&role_id=  (QUERY!)
//        user_id accepts a Firebase UID OR an email (backend resolves email→UID)
// DELETE /permissions/facilities/{facilityId}/staff/{userId}
// ---------------------------------------------------------------------------

export function listFacilityStaff(
  facilityId: string,
): Promise<FacilityAssignment[]> {
  return httpClient.get<FacilityAssignment[]>(
    `/permissions/facilities/${facilityId}/staff`,
  );
}

export function assignFacilityStaff(
  facilityId: string,
  body: { user_id: string; role_id: string },
): Promise<FacilityAssignment> {
  const qs = new URLSearchParams({
    user_id: body.user_id,
    role_id: body.role_id,
  });
  return httpClient.post<FacilityAssignment>(
    `/permissions/facilities/${facilityId}/staff?${qs.toString()}`,
  );
}

export function removeFacilityStaff(
  facilityId: string,
  userId: string,
): Promise<void> {
  return httpClient.delete<void>(
    `/permissions/facilities/${facilityId}/staff/${encodeURIComponent(userId)}`,
  );
}

export function resolveStaffIdentities(
  orgId: string,
  userIds: string[],
): Promise<StaffIdentityResolution> {
  return httpClient.post<StaffIdentityResolution>(
    `/permissions/orgs/${orgId}/staff/resolve`,
    { user_ids: userIds },
  );
}

// ---------------------------------------------------------------------------
// Permission check (server-computed RBAC)
// GET /permissions/check?facility_id=&action=
// Returns { allowed } — whether the authenticated user holds `action` at the
// facility. The SERVER owns the decision (backend `permission.can()`); the FE
// must never derive a permission client-side. Used to gate operator-only
// controls (e.g. the adjustable refund on a booking cancel needs
// `facility:refund_override`).
// ---------------------------------------------------------------------------

export function checkFacilityPermission(
  facilityId: string,
  action: string,
): Promise<{ allowed: boolean }> {
  return httpClient.get<{ allowed: boolean }>(
    `/permissions/check?facility_id=${facilityId}&action=${encodeURIComponent(action)}`,
  );
}

// ---------------------------------------------------------------------------
// Caller's facility action set (server-computed RBAC)
// GET /permissions/facilities/{facilityId}/my-actions -> { actions: string[] }
// Returns the CALLER's facility-scoped actions at this facility (backend #544;
// owner -> all, non-member -> []). The role-aware business sidebar fetches this
// ONCE and shows only the nav sections whose required action is present, instead
// of the full owner chrome for every role. The user is derived from the token
// (never a query param), so it always returns the caller's OWN actions.
//
// This is a CLIENT HINT for nav VISIBILITY only — the server still enforces
// every action on the real endpoints (a hidden route reached by URL still 403s).
// ---------------------------------------------------------------------------

export type FacilityActions = Schema["FacilityActionsResponse"];

export function getMyFacilityActions(
  facilityId: string,
): Promise<FacilityActions> {
  return httpClient.get<FacilityActions>(
    `/permissions/facilities/${facilityId}/my-actions`,
  );
}

// ---------------------------------------------------------------------------
// Staff GROUPS — the Identity-Center model. A group has MEMBERS (users) and
// ROLE ASSIGNMENTS (a role, optionally scoped to one facility; null = org-wide).
// Sourced from the generated OpenAPI types (ADR-005) so the shapes never drift
// from backend common/models.py.
//
// Backend contract (app/routers/permission.py, prefix "/permissions") — NOTE the
// MIXED body shapes, matched EXACTLY below:
//   GET    /orgs/{orgId}/groups                 -> StaffGroupResponse[]
//   POST   /orgs/{orgId}/groups   body {name}   -> StaffGroupResponse       (JSON body)
//   PATCH  /groups/{groupId}      body {name}   -> StaffGroupResponse       (JSON body)
//   DELETE /groups/{groupId}                    -> 204
//   GET    /groups/{groupId}/members            -> StaffGroupMemberResponse[]
//   POST   /groups/{groupId}/members?user_id=   -> StaffGroupMemberResponse (QUERY param, NO body;
//                                                  backend resolves email -> uid)
//   DELETE /groups/{groupId}/members/{userId}   -> 204
//   GET    /groups/{groupId}/roles              -> GroupRoleAssignmentResponse[]
//   POST   /groups/{groupId}/roles?role_id=[&facility_id=]                  (QUERY params, NO body;
//                                                  facility_id OMITTED = org-wide)
//                                              -> GroupRoleAssignmentResponse
//   DELETE /groups/{groupId}/roles/{assignmentId} -> 204
//
// The QUERY-param shape (addGroupMember / assignRoleToGroup) mirrors the existing
// assignFacilityStaff / inviteOrgMember convention (URLSearchParams), NOT a JSON
// body. 403 (escalation) / 409 (duplicate) propagate — the caller never swallows.
// ---------------------------------------------------------------------------

export type StaffGroup = StaffGroupResponse;
export type StaffGroupMember = StaffGroupMemberResponse;
export type GroupRoleAssignment = GroupRoleAssignmentResponse;

export function listGroups(orgId: string): Promise<StaffGroup[]> {
  return httpClient.get<StaffGroup[]>(`/permissions/orgs/${orgId}/groups`);
}

export function createGroup(
  orgId: string,
  body: { name: string },
): Promise<StaffGroup> {
  return httpClient.post<StaffGroup>(`/permissions/orgs/${orgId}/groups`, body);
}

export function updateGroup(
  groupId: string,
  body: { name: string },
): Promise<StaffGroup> {
  return httpClient.patch<StaffGroup>(`/permissions/groups/${groupId}`, body);
}

export function deleteGroup(groupId: string): Promise<void> {
  return httpClient.delete<void>(`/permissions/groups/${groupId}`);
}

export function listGroupMembers(groupId: string): Promise<StaffGroupMember[]> {
  return httpClient.get<StaffGroupMember[]>(
    `/permissions/groups/${groupId}/members`,
  );
}

// POST /permissions/groups/{groupId}/members?user_id=… — user_id is a QUERY
// param (NO JSON body). user_id accepts a Firebase UID OR an email; the backend
// resolves an email to a UID server-side. URLSearchParams percent-encodes the
// value so e.g. "a+b@example.com" round-trips correctly (the raw '+' must not
// decode to a space).
export function addGroupMember(
  groupId: string,
  body: { user_id_or_email: string },
): Promise<StaffGroupMember> {
  const qs = new URLSearchParams({ user_id: body.user_id_or_email });
  return httpClient.post<StaffGroupMember>(
    `/permissions/groups/${groupId}/members?${qs.toString()}`,
  );
}

export function removeGroupMember(
  groupId: string,
  userId: string,
): Promise<void> {
  return httpClient.delete<void>(
    `/permissions/groups/${groupId}/members/${encodeURIComponent(userId)}`,
  );
}

export function listGroupRoleAssignments(
  groupId: string,
): Promise<GroupRoleAssignment[]> {
  return httpClient.get<GroupRoleAssignment[]>(
    `/permissions/groups/${groupId}/roles`,
  );
}

// POST /permissions/groups/{groupId}/roles?role_id=…[&facility_id=…] — role_id
// (and optional facility_id) are QUERY params (NO JSON body). For an ORG-WIDE
// assignment facility_id is OMITTED ENTIRELY — never sent as the string "null".
export function assignRoleToGroup(
  groupId: string,
  body: { role_id: string; facility_id?: string },
): Promise<GroupRoleAssignment> {
  const qs = new URLSearchParams({ role_id: body.role_id });
  if (body.facility_id) qs.set("facility_id", body.facility_id);
  return httpClient.post<GroupRoleAssignment>(
    `/permissions/groups/${groupId}/roles?${qs.toString()}`,
  );
}

export function revokeRoleFromGroup(
  groupId: string,
  assignmentId: string,
): Promise<void> {
  return httpClient.delete<void>(
    `/permissions/groups/${groupId}/roles/${assignmentId}`,
  );
}
